import Alpine from 'alpinejs';
import AlpineFocus from '@alpinejs/focus';
import AlpineResize from '@alpinejs/resize';
import {disableBodyScroll, enableBodyScroll} from 'body-scroll-lock-upgrade';
import {formatMoney} from "@shopify/theme-currency";
import qs from 'qs';

function prepareBody(data) {
    return qs.stringify(data);
}

function createUrl(url, params) {
    const query = new URLSearchParams(params).toString();
    return query ? `${url}?${query}` : url;
}

async function fetchWithHandling(url, options) {
    const response = await fetch(url, options);
    const contentType = response.headers.get('content-type') || '';
    if (!response.ok || !contentType.includes('application/json')) {
        throw new Error(await response.text());
    }
    return response.json();
}


import '@/scripts/sections/announcement-bar';
import '@/scripts/sections/footer';
import '@/scripts/components/cartAdd';
import {smsPromoPopup, smsPromoSuccess} from '@/scripts/components/smsPromo';
import {start as startSections} from '@/scripts/core/sections';

// Section scripts (src/scripts/sections/types/*), each loaded when its root's data-section-loading says.
startSections();

Alpine.plugin(AlpineFocus);
Alpine.plugin(AlpineResize);

Alpine.directive('body-scroll-lock', (element, {expression}, {evaluateLater, effect}) => {
    const evaluate = evaluateLater(expression);
    if (!evaluate) {
        return;
    }

    effect(() => {
        evaluate((expression_value) => {
            enableBodyScroll(element);

            if (!expression_value) {
                return;
            }

            disableBodyScroll(element, {
                reserveScrollBarGap: true,
                allowTouchMove(el) {
                    while (el && el !== document.body) {
                        if ([
                            el?.getAttribute('data-product-info-modal') !== null,
                            el?.getAttribute('data-allow-scroll') !== null,
                            el?.getAttribute('data-simplebar') !== null,
                            !!el?.classList?.contains('simplebar-mask'),
                            !!el?.classList?.contains('swiper'),
                            !!el?.classList?.contains('swiper-wrapper'),
                        ]?.includes(true)) {
                            return true;
                        }

                        el = el?.parentElement;
                    }

                    return undefined;
                }
            });
        });
    });
});

Alpine.directive('remove', (el, {expression}, {evaluateLater, effect}) => {
    if (!expression) {
        // no expression -> remove immediately
        el.remove();

        return;
    }

    const shouldRemove = evaluateLater(expression)

    effect(() => {
        shouldRemove((value) => {
            if (!value) {
                return;
            }

            el.remove();
        })
    });
});

Alpine.directive('money', (el, {expression, modifiers}, {evaluateLater, effect}) => {
    const getValue = evaluateLater(expression);

    effect(() => {
        getValue((raw) => {
            const value = parseInt(raw);

            let format = window.Shopify.money_format || '${{amount}}';

            if (!!modifiers?.includes('amount')) {
                format = format.replace('{{amount}}', '{{amount}}');
            }

            if (!!modifiers?.includes('amount_no_decimals')) {
                format = format.replace('{{amount}}', '{{amount_no_decimals}}');
            }

            if (!!modifiers?.includes('amount_with_comma_separator')) {
                format = format.replace('{{amount}}', '{{amount_with_comma_separator}}');
            }

            if (!!modifiers?.includes('amount_no_decimals_with_comma_separator')) {
                format = format.replace('{{amount}}', '{{amount_no_decimals_with_comma_separator}}');
            }

            el.textContent = formatMoney(value, format);
        })
    });
});

// Styled <select> (product card variant picker). No search box; the dropdown stays inside the
// wrapper, and Tom Select keeps the native <select> value in sync for the form submit.
Alpine.directive('tom-select', (el, {}, {cleanup}) => {
    let instance = null;

    import('tom-select/base')
        .then(({default: TomSelect}) => {
            if (!el.isConnected || el.tomselect) {
                return;
            }

            instance = new TomSelect(el, {controlInput: null});
        })
        .catch((error) => console.error(error));

    cleanup(() => instance?.destroy());
});

Alpine.magic('utils', () => {
    return {
        formatMoney,
    };
})

document.addEventListener('DOMContentLoaded', () => {
    document.body.classList.remove('preload');

    Alpine.data('headerWrapper', () => {
        return {
            height: 0,

            init() {
                const container = this.$root;
                if (!container) {
                    return;
                }

                // x-resize's ResizeObserver callback writes --header-group-height, which drives
                // .site-content's margin-top — that can toggle the scrollbar and resize this fixed,
                // width:100% wrapper, re-arming the observer inside its own delivery cycle. Rounding
                // (see theme.liquid) plus this guard make the second delivery a no-op, so the loop
                // terminates at depth 2. Kept synchronous on purpose: deferring to rAF would land the
                // value a frame late and paint one frame with the stale inline seed.
                let written = null;

                this.$watch('height', (height) => {
                    if (!height || height === written) {
                        return;
                    }

                    written = height;
                    document.documentElement.style.setProperty('--header-group-height', `${height}px`);
                    document.documentElement.dataset.headerGroupHeight = height;
                });

                // $width/$height exist only inside the x-resize expression scope, not as
                // magics — this.$height is undefined here, so measure directly instead.
                this.height = Math.round(container.getBoundingClientRect().height);
            }
        };
    });

    Alpine.data('header', () => {
        return {
            opened: false,
            menu: null,

            open(id) {
                this.menu = id;
                this.opened = true;
            },

            close() {
                this.opened = false;
                this.menu = null;
            },

            toggle(id) {
                const opened = this.opened || false;

                if (opened) {
                    this.close();
                } else {
                    this.open(id);
                }
            }
        }
    });

    Alpine.data('productCard', () => {
        return {
            initialized: false,

            data: {
                product: null,
                selected_variant: null,
            },

            init() {
                const container = this.$el;

                const productElement = container.querySelector('script[type="application/json"]');
                const product = JSON.parse(productElement?.innerHTML?.trim() || '{}')

                this.data = {
                    product,
                    selected_variant: product.selected_variant || product.variants?.[0],
                };

                this.initProductForm();

                this.initialized = true;
            },

            get product() {
                const data = this.data;
                if (!data) {
                    return null;
                }

                return data?.product || null;
            },

            get selected_variant() {
                const data = this.data;
                if (!data) {
                    return null;
                }

                return data?.selected_variant || null;
            },

            set selected_variant(variant) {
                this.data = {
                    ...this.data,
                    selected_variant: variant,
                };
            },

            initProductForm() {
                const container = this.$el;
                if (!container) {
                    return;
                }

                const form = container.querySelector('[data-product-form]');
                if (!form) {
                    return;
                }

                const onVariantChange = this.onVariantChange.bind(this);

                // Only product cards need ProductForm (and @shopify/theme-product), so it loads
                // on demand instead of shipping in the every-page bundle. Submission is handled
                // natively by the delegated [data-cart-form] handler in @/scripts/components/cartAdd.
                import('@/scripts/core/ProductForm')
                    .then(({default: ProductForm}) => {
                        new ProductForm(form, this.product, {
                            onVariantChange,
                        });
                    })
                    .catch((error) => console.error('[productCard] ProductForm failed to load', error));
            },

            onVariantChange(event) {
                const variant = event?.dataset?.variant;

                this.selected_variant = variant ? {
                    ...variant
                } : null;
            },

            get featured_image() {
                const variant = this.selected_variant;
                if (!variant) {
                    return null;
                }

                return variant?.featured_image || null;
            },

            get background_image() {
                const variant = this.selected_variant;
                if (!variant) {
                    return null;
                }

                return variant?.background_image || null;
            },

            get available() {
                const variant = this.selected_variant;
                if (!variant) {
                    return false;
                }

                return variant?.available || false;
            },

            get price() {
                const variant = this.selected_variant;
                if (!variant) {
                    return null;
                }

                return variant?.price || null;
            },

            get compare_at_price() {
                const variant = this.selected_variant;
                if (!variant) {
                    return 0;
                }

                return variant?.compare_at_price || 0;
            },

            get button_text() {
                const variant = this.selected_variant;
                if (!variant) {
                    return 'Unavailable';
                }

                if (variant?.waitlist) {
                    return 'Waitlist';
                }

                return !!variant?.available ? 'Add' : 'Sold';
            }
        };
    });

    Alpine.data('smsPromoPopup', smsPromoPopup);
    Alpine.data('smsPromoSuccess', smsPromoSuccess);

    window.Alpine = Alpine;

    Alpine.start();
});
